import { ui } from '@beelday/common'
import styled from '@emotion/styled'
import { useSelector } from 'app-redux'
import { Colors } from 'common/presentation/colors'
import { useOrganization } from 'organization/use-organization-branding'
import { FC } from 'react'
import { use100vh } from 'react-div-100vh'
import { RoomContainer } from 'room/common/presentation/room-container'
import { Menu } from 'room/common/presentation/menu'
import { ResumeTrainingAction } from 'room/common/presentation/resume-training-menu-item'
import useIsTrainer from 'room/common/use-is-trainer'
import { MiscPanel } from 'room/misc'
import MiscPanelMenu from 'room/misc/misc-panel-menu'
import { StageControls } from 'room/common/stage-surface'
import { ConfiguredLobbyHero } from './configured-lobby-in-person'
import { useInPersonLobbyMoment } from './in-person-lobby-moment'
import LobbyHeader from './lobby-header'

/**
 * Where the card sits: centred between the header and the menu, and scrolling
 * by itself on a screen too short for it, so the menu never leaves the page.
 */
const Body = styled.main`
	display: flex;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 16px 70px;
`

const BottomMenuContainer = styled.div`
	margin-top: 10px;
`

/**
 * The in-person lobby: before the start and during breaks.
 *
 * `TrainingBreakPhase` still sends everybody to the lobby, but a stationary
 * interaction scheme builds that lobby with no public chat rooms and no video,
 * so the ordinary lobby renders an empty room list and an instruction to join
 * a room that is not there. There is nowhere to go during an in-person break -
 * everybody just stands up - so the screen says so instead.
 *
 * One card for every audience (`ConfiguredLobbyHero`): the trainer's checklist
 * and Start or End break, the participants' "we are waiting". The trainer
 * additionally keeps the Resume action in the menu, which their presentation
 * clicker can also press to end a break. The projector of presenter mode draws
 * this same screen, larger and without the menu.
 */
export const LobbyInPerson: FC = () => {
	const containerHeight = use100vh()
	const training = useSelector(state => state.sessionInfo.training)
	const organization = useOrganization(training?.organizationId || '')
	const isTrainer = useIsTrainer()

	/**
	 * Before the start or during a break - the same lobby either way. The time
	 * comes from the script, which is optional, so it is often simply absent.
	 */
	const lobby = useInPersonLobbyMoment()
	const { moment } = lobby

	return (
		<RoomContainer
			style={{
				backgroundColor: `${Colors.paleGray}`,
				height: `${containerHeight}px`,
			}}
			data-testid={
				moment === 'not_started' ? 'in_person.not_started' : 'in_person.break'
			}
		>
			<LobbyHeader
				training={training}
				organization={organization}
				showJoinRoomHint={false}
			/>
			<Body>
				<ConfiguredLobbyHero layout="desktop" lobby={lobby} />
			</Body>
			{/* The projector of presenter mode shows the card only. */}
			<StageControls>
				<BottomMenuContainer>
					<Menu>
						<MiscPanelMenu />
						{isTrainer ? (
							<ResumeTrainingAction moment={moment} clicker />
						) : null}
					</Menu>
				</BottomMenuContainer>
			</StageControls>
			<MiscPanel />
			<ui.BeeldayLogoAbsoluteBottom />
		</RoomContainer>
	)
}

export default LobbyInPerson
